/**
 * ORTAK TARİH / SAAT BİÇİMİ — UI/UX Dalga 1 (mutabakat K7, K11)
 *
 * ================================================================
 * NEDEN ORTAK
 * ================================================================
 * Ölçüldü (05.10.2026): aynı hafta panelde "2026-10-05 – 2026-10-11",
 * görev formunda "10-07", velide "5 – 11 Ekim 2026" yazıyordu; koçluk
 * görüşmesi saati "05.10.2026 18:00:00" — saniyesiyle. Her ekran kendi
 * `toLocaleString`ini çağırınca biçim ekran sayısı kadar çoğalıyor.
 *
 * ================================================================
 * AN mı, TAKVİM GÜNÜ mü — İKİ AYRI AİLE
 * ================================================================
 * İkisi de `string` görünüyor ama anlamları farklı (ayrıntı
 * `apps/ogrenci/src/veli-hafta.ts` başlığında, C14.1 gece yarısı sınıfı):
 *
 *   · AN (`createdAt`, görüşme saati): gerçek bir zaman noktası →
 *     Europe/Istanbul'a çevrilir. Tarayıcının saat dilimine bırakılmaz:
 *     yurt dışındaki bir koç aynı görüşmeyi farklı saatte görmesin.
 *   · TAKVİM GÜNÜ ("2026-10-07" ya da `yerelGunBasi`nin UTC gece yarısı
 *     damgası): bir gün, an değil → `timeZone: "UTC"` ile biçimlenir;
 *     yerele çevrilirse UTC'nin gerisindeki dilimde bir gün geri kayar.
 *
 * Biçimlendiriciler ÇAĞRI BAŞINA kuruluyor: `Intl.DateTimeFormat` saat
 * dilimini oluşturulduğu anda donduruyor ve modül düzeyinde kurulunca
 * `TZ` değiştiren mutasyon testi hatayı göremiyordu (veli-hafta.ts'te
 * ölçüldü).
 */

// `@koclukpanel/contracts`taki `SAAT_DILIMI` ile AYNI değer. `ui` paketi
// contracts'a bağımlı değil (sunum katmanı); tek sabit için bağımlılık
// eklemek yerine değer burada tekrar ediliyor ve dışa aktarılmıyor.
const SAAT_DILIMI = "Europe/Istanbul";

function bicim(secenekler: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  return new Intl.DateTimeFormat("tr-TR", secenekler);
}

function an(deger: string | Date): Date | null {
  const d = deger instanceof Date ? deger : new Date(deger);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** "2026-10-07" ya da "2026-10-07T00:00:00.000Z" → UTC gece yarısı damgası. */
function takvimGunu(deger: string): Date | null {
  return an(/^\d{4}-\d{2}-\d{2}$/.test(deger) ? `${deger}T00:00:00.000Z` : deger);
}

/** AN → "5 Ekim 2026 18:00" (İstanbul, saniyesiz). Çözülemezse "—". */
export function anMetni(deger: string | Date): string {
  const d = an(deger);
  if (!d) return "—";
  return bicim({
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: SAAT_DILIMI,
  }).format(d);
}

/** AN → "18:00" (İstanbul, saniyesiz). Çözülemezse "—". */
export function saatMetni(deger: string | Date): string {
  const d = an(deger);
  if (!d) return "—";
  return bicim({ hour: "2-digit", minute: "2-digit", timeZone: SAAT_DILIMI }).format(d);
}

/** TAKVİM GÜNÜ → "7 Ekim" (`yil` ile "7 Ekim 2026"). Çözülemezse "—". */
export function gunMetni(deger: string, secenek: { yil?: boolean } = {}): string {
  const d = takvimGunu(deger);
  if (!d) return "—";
  return bicim({
    day: "numeric",
    month: "long",
    ...(secenek.yil ? { year: "numeric" } : {}),
    timeZone: "UTC",
  }).format(d);
}

/**
 * TAKVİM GÜNÜ aralığı — tekrarı olmayan en kısa biçim:
 * "7 – 13 Eylül" · "28 Eylül – 4 Ekim" · "28 Aralık 2026 – 3 Ocak 2027".
 *
 * `yil`:
 *   · "her-zaman" — velinin özet başlığı: hafta rakamlarının hangi yıla
 *     ait olduğu her zaman yazılı.
 *   · "gerekirse" — koçun program gezinmesi: içinde bulunulan yılda yıl
 *     yazmak başlığı uzatıyor (390 px'te düğmeler arasına sığmıyordu);
 *     başka bir yıla geçilince ya da aralık yıl değiştirince yazılıyor.
 *
 * Çözülemeyen değerde `null`: uydurma yok — yanlış bir aralık, hiç
 * aralık olmamasından kötüdür.
 */
export function gunAraligiMetni(
  bas: string,
  son: string,
  secenek: { yil: "her-zaman" | "gerekirse" },
): string | null {
  const b = takvimGunu(bas);
  const s = takvimGunu(son);
  if (!b || !s) return null;

  const GUN = bicim({ day: "numeric", timeZone: "UTC" });
  const GUN_AY = bicim({ day: "numeric", month: "long", timeZone: "UTC" });
  const GUN_AY_YIL = bicim({ day: "numeric", month: "long", year: "numeric", timeZone: "UTC" });

  const ayniYil = b.getUTCFullYear() === s.getUTCFullYear();
  const ayniAy = ayniYil && b.getUTCMonth() === s.getUTCMonth();
  const buYil = Number(bicim({ year: "numeric", timeZone: SAAT_DILIMI }).format(new Date()));
  const yilYaz = secenek.yil === "her-zaman" || !ayniYil || b.getUTCFullYear() !== buYil;

  if (!ayniYil) return `${GUN_AY_YIL.format(b)} – ${GUN_AY_YIL.format(s)}`;
  const sonMetin = (yilYaz ? GUN_AY_YIL : GUN_AY).format(s);
  if (ayniAy) return `${GUN.format(b)} – ${sonMetin}`;
  return `${GUN_AY.format(b)} – ${sonMetin}`;
}
